// CockpitPreviewWidget - Message flottant discret style Bitrix24
// Petite bulle avec avatar + message qui reste visible jusqu'à fermeture
// S'affiche sur les pages marketing (Espace Pro, etc.) mais PAS dans les Cockpits
// Rotation automatique des phrases pour plus d'engagement

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

// Phrases pour les particuliers
const PHRASES_PARTICULIER = [
  {
    title: "Besoin d'un coup de main ?",
    text: "Découvre comment choisir les meilleurs artisans pour ton projet.",
  },
  {
    title: "Je t'aide à trouver...",
    text: "Les matériaux, accessoires ou produits que tu cherches.",
  },
  {
    title: "Besoin d'un assistant ?",
    text: "Je suis là pour t'accompagner dans tous tes projets.",
  },
];

// Phrases pour les pros
const PHRASES_PRO = [
  {
    title: "Tu hésites sur le plan ?",
    text: "Notre assistant IA t'aide à choisir l'offre adaptée à ton activité.",
  },
  {
    title: "Développe ton business",
    text: "Découvre comment attirer plus de clients avec nos outils.",
  },
  {
    title: "Besoin d'aide ?",
    text: "Je t'accompagne dans la prise en main de ton espace pro.",
  },
];

export default function CockpitPreviewWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  // Pages où le widget ne doit PAS apparaître
  const hiddenPaths = [
    '/cockpit-pro',
    '/cockpit-particulier',
    '/cockpit-demo',
    '/connexion',
    '/inscription',
    '/login',
    '/register',
    '/decrire-projet',
  ];
  
  // Vérifier si on doit masquer
  const shouldHide = hiddenPaths.some(path => location.pathname.startsWith(path));
  
  // Déterminer le contexte
  const isInEspacePro = location.pathname.startsWith('/pro');
  const cockpitPath = isInEspacePro ? '/cockpit-pro' : '/cockpit-particulier';
  
  // Phrases selon le contexte
  const phrases = isInEspacePro ? PHRASES_PRO : PHRASES_PARTICULIER;
  
  // Index de la phrase actuelle (rotation)
  const [phraseIndex, setPhraseIndex] = useState(0);
  
  // Rotation automatique des phrases toutes les 5 secondes
  useEffect(() => {
    if (!isVisible || dismissed) return;
    
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isVisible, dismissed, phrases.length]);
  
  // Phrase actuelle
  const currentPhrase = phrases[phraseIndex];
  
  // Détecter si on est sur desktop (lg+)
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );
  
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Vérifier si déjà fermé pour cette session
  useEffect(() => {
    const dismissedKey = `cockpitWidget_${isInEspacePro ? 'pro' : 'particulier'}_dismissed`;
    const wasDismissed = sessionStorage.getItem(dismissedKey);
    if (wasDismissed) {
      setDismissed(true);
    } else {
      // Afficher après 2 secondes
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isInEspacePro, location.pathname]);
  
  // Ouvrir le cockpit complet
  const handleOpenCockpit = () => {
    navigate(cockpitPath);
  };
  
  // Fermer définitivement
  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    setIsVisible(false);
    const dismissedKey = `cockpitWidget_${isInEspacePro ? 'pro' : 'particulier'}_dismissed`;
    sessionStorage.setItem(dismissedKey, 'true');
  };
  
  // Ne pas afficher sur mobile ou dans les cockpits
  if (!isDesktop || shouldHide || dismissed || !isVisible) return null;
  
  // Configuration selon le contexte (avatar et couleur fixes, texte rotatif)
  const config = isInEspacePro 
    ? {
        avatar: "🤖",
        accentColor: "#1E3A5F",
      }
    : {
        avatar: "👋",
        accentColor: "#E8650A",
      };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 20, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 20, scale: 0.95 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed z-[9970] hidden lg:block"
        style={{
          bottom: '160px', // Remonté pour être au niveau du bouton micro
          right: '90px', // Décalé à gauche des boutons FAB
          maxWidth: '260px',
        }}
        data-testid="cockpit-preview-widget"
      >
        {/* Bulle de message style Bitrix24 */}
        <div 
          className="relative bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer group"
          style={{
            boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.08)',
            border: `2px solid ${config.accentColor}22`,
          }}
          onClick={handleOpenCockpit}
        >
          {/* Bouton fermer */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
            title="Fermer"
          >
            <X size={14} />
          </button>

          {/* Contenu */}
          <div className="p-4 pr-8">
            <div className="flex gap-3">
              {/* Avatar */}
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                style={{ 
                  background: `linear-gradient(135deg, ${config.accentColor}22, ${config.accentColor}11)`,
                  border: `2px solid ${config.accentColor}33`,
                }}
              >
                {config.avatar}
              </div>

              {/* Texte rotatif avec animation */}
              <div className="flex-1 min-w-0">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={phraseIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h4 
                      className="font-semibold text-gray-900 text-[13px] mb-0.5 leading-tight"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {currentPhrase.title}
                    </h4>
                    <p 
                      className="text-gray-500 text-[11px] leading-relaxed"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {currentPhrase.text}
                    </p>
                  </motion.div>
                </AnimatePresence>
                
                {/* Indicateurs de pagination */}
                <div className="flex gap-1 mt-2">
                  {phrases.map((_, idx) => (
                    <div
                      key={idx}
                      className="h-1 rounded-full transition-all duration-300"
                      style={{
                        width: idx === phraseIndex ? '16px' : '6px',
                        background: idx === phraseIndex ? config.accentColor : `${config.accentColor}33`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Flèche pointant vers les boutons FAB (à droite) */}
          <div 
            className="absolute top-1/2 -right-2 w-3 h-3 bg-white rotate-45 -translate-y-1/2"
            style={{ 
              boxShadow: '2px -2px 4px rgba(0,0,0,0.05)',
              border: `1px solid ${config.accentColor}22`,
              borderLeft: 'none',
              borderBottom: 'none',
            }}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
